<template lang="pug">
div
	h1 {{contentpage.name}}
	ws-content(:value="contentpage.content")
		template(slot="reachability")
			v-row(v-if="departures.city.distance !== 9999")
				v-col
					h2 {{$t('page.resorts._resort.reachability:by_car.title')}}
					v-row(no-gutters)
						v-col.shrink
							ws-icon(small) angle-right
						v-col
							i18n(path="page.resorts._resort.reachability:by_car.text_{distance}_{city}_{map}_{highway}" )
								template(#highway) {{departures.highway.distance}}
								template(#city) {{departures.city.node.name}}
								template(#distance) {{departures.city.distance}}
								template(#map)
									a(:href="`https://www.google.nl/maps/dir/${departures.city.node.name}/${geography.location.coordinates[1]},${geography.location.coordinates[0]}`" target="_blank") {{$t('page.resorts._resort.reachability:show_route_link')}}
			template(v-if="gateways.length")
				h3.mt-6(v-if="gateways.length > 1") {{$t('page.resorts._resort.reachability:gateways_title')}}
				v-row(no-gutters v-for="(gateway, index) in gateways" :key="gateway.id")
					v-col.shrink
						ws-icon(small) angle-right
					v-col {{gateway.content.markdown.replace('{prefix}', gatewayPrefix(index))}}
			template(v-if="bottlenecks.length")
				h3.mt-6(v-if="bottlenecks.length > 1") {{$t('page.resorts._resort.reachability:bottlenecks_title')}}
				v-row(no-gutters v-for="(bottleneck, index) in bottlenecks" :key="bottleneck.id")
					v-col.shrink
						ws-icon(small) angle-right
					v-col {{bottleneck.content.markdown.replace('{prefix}', name)}}
			template(v-if="departures.airports.length")
				h2.mt-6 {{$t('page.resorts._resort.reachability:airports_around_{resort}', {resort: name})}}

				airline-prices(:airports="departures.airports")
				client-only
					mapbox.mt-6(
						ref="map"
						style="height:600px"
						:layers="layers"
						:sources="sources"
						:fitbounds="markers.features.map(e=>e.geometry.coordinates)"
						:geolocateControl="{ show: true, position: 'top-right', options: {}}"
						@map-styleload="mapStyle")
						div(slot="popup")
							span.h3(v-if="popupData.__typename == 'Resort'") {{popupData.name}}
							span.h3(v-if="popupData.__typename == 'Airport'") {{popupData.name}} ({{$t('page.resorts._resort.reachability:{time}_to_drive', {time: formatMinutes(popupData.minutes)})}})
		.flow-left(slot="checklist")
			ws-checklist(:resort-id="id")
</template>

<script>
import gql from 'graphql-tag';
import { ref } from '@vue/composition-api';
import { mapGetters } from 'vuex';
import Content from '@dengl/common/components/content.vue';
import areasReachability from '@/mixins/areas-reachability.js';
import MetaFragment from '@/components/graphql/metaFragment.graphql';
import useMap from '@/use/map';
import AirlinePrices from '@/components/airports/airline-prices';

const getResortReachability = gql`
	query getResortReachability(
		$slug: String!
		$preferedCity: String!
		$path: String
		$fallback: String
	) {
		contentpage(path: $path, fallback: $fallback) {
			id
			name
			slug
			content {
				...ContentDataFragment
			}
			...MetaFragment
		}
		resort(slug: $slug) {
			id
			name
			slug
			country {
				id
				name
				countryCode
				slug
			}
			region {
				id
				name
				slug
			}
			area {
				id
				name
				slug
			}
			hasTrainStation
			geography {
				location {
					type
					coordinates
				}
			}
			departures {
				highway {
					distance
				}
				city(city: $preferedCity) {
					distance
					node {
						id
						name
						slug
						geography {
							location {
								type
								coordinates
							}
						}
					}
				}
				airports {
					distance
					minutes
					node {
						id
						name
						slug
						flights
						airlines {
							url
							price
							airline {
								id
								name
							}
						}
						geography {
							location {
								type
								coordinates
							}
						}
					}
				}
			}
			gateways {
				id
				name
				content {
					markdown(raw: true)
				}
			}
			bottlenecks {
				id
				name
				content {
					markdown(raw: true)
				}
			}
		}
	}
	${MetaFragment}
	${Content.fragments}
`;

export default {
	nuxtI18n: {
		paths: {
			'nl-NL': '/dorpen/:resort/bereikbaarheid',
			'de-DE': '/skiorte/:resort/erreichbarkeit',
			'nl-BE': '/dorpen/:resort/bereikbaarheid'
		}
	},
	components: {
		AirlinePrices,
		Mapbox: () => (process.client ? import('@/components/mapbox.vue') : null)
	},
	mixins: [areasReachability],
	layout: 'default',
	asyncData({ params, app, route }) {
		return app.$graphClient
			.query({
				query: getResortReachability,
				variables: {
					slug: params.resort,
					preferedCity: app.store.getters.preferedCity,
					path: route.path,
					fallback: route.path.replace(params.resort, '_resort')
				},
				errorPolicy: 'all'
			})
			.then(({ data, errors }) => {
				if (!data.contentpage) {
					return app.httpError(
						404,
						null,
						'resorts-resort-reachability-01',
						errors
					);
				}
				if (errors) {
					throw errors[0];
				}

				app.interpolater(data.contentpage.meta, ['title', 'description']);
				app.interpolater(data.contentpage.content, ['markdown']);
				app.interpolater(data.contentpage, ['name']);

				app.meta(data.contentpage.meta);
				app.ads({
					sidebar: {
						visible: true,
						targets: {
							Village: data.resort.id,
							Skiresort: data.resort.area.id,
							State: data.resort.region.id,
							Country: data.resort.country.id
						}
					}
				});
				app.resortBreadcrumbs(data.resort, {
					[app.i18n.t(`page.resorts._resort.reachability:breadcrumb`)]: {
						name: 'resorts-resort-reachability',
						params: { resort: data.resort.slug }
					}
				});

				return { ...data.resort, contentpage: data.contentpage };
			})
			.catch(app.$destinationNotFoundCatcher)
			.catch(error => {
				return app.httpError(
					500,
					null,
					'resorts-resort-reachability-02',
					error
				);
			});
	},
	data() {
		return {
			reachabilityAirportTableHeaders: [
				{
					text: '',
					value: 'name'
				},
				{
					text: this.$t(
						'page.resorts._resort.reachability:airports_table.distance_header'
					),
					value: 'distance'
				},
				{
					text: this.$t(
						'page.resorts._resort.reachability:airports_table.time_header'
					),
					value: 'minutes'
				},
				{
					text: this.$t(
						'page.resorts._resort.reachability:airports_table.flights_header'
					),
					value: 'node.flights'
				}
			]
		};
	},
	computed: {
		...mapGetters(['preferedCity'])
	},
	created() {
		this.$emit('update:showSidebar', true);
	},
	setup() {
		const combinedLocations = ref([]);
		const { markers, sources, attachListeners, popupData } =
			useMap(combinedLocations);

		const layers = [
			{
				id: 'airport-markers-icons',
				type: 'symbol',
				source: 'geojson-markers',
				layout: {
					'icon-image': ['get', 'icon'],
					'icon-size': 0.4,
					'icon-anchor': 'bottom',
					'icon-allow-overlap': true
				},
				minzoom: 0,
				maxzoom: 24
			}
		];

		return {
			combinedLocations,
			sources,
			layers,
			markers,
			attachListeners,
			popupData
		};
	},
	mounted() {
		this.combinedLocations = [
			{
				__typename: 'Resort',
				geography: this.geography,
				id: this.id,
				name: this.name,
				slug: this.slug
			},
			...this.departures.airports.map(airport => {
				return { ...airport.node, minutes: airport.minutes };
			})
		];
		if (this.$refs.map) {
			this.$nextTick(() => {
				this.$refs.map.fitToCurrentBounds();
			});
		}
	},
	methods: {
		mouseClick(feature) {
			switch (feature.properties.__typename) {
				case 'Resort':
					this.$router.push(
						this.localePath({
							name: 'resorts-resort',
							params: { resort: feature.properties.slug }
						})
					);
					break;
				case 'Airport':
					this.$router.push(
						this.localePath({
							name: 'airports-airport',
							params: { airport: feature.properties.slug }
						})
					);
					break;
			}
		},
		mapStyle(map) {
			this.$refs.map.fitToCurrentBounds();
			this.attachListeners(
				map,
				this.$refs.map,
				'airport-markers-icons',
				'geojson-markers',
				this.mouseClick
			);
		}
	},
	head() {
		return {
			...this.$structuredData()
		};
	}
};
</script>
